import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { showError, showSuccess, showWarning } from '../utils/toast';

const ExamInstructions = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agreed, setAgreed] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const [canTakeExam, setCanTakeExam] = useState(true);
  const [cameraPermission, setCameraPermission] = useState(null);
  const [locationPermission, setLocationPermission] = useState(null);
  const [requestingPermissions, setRequestingPermissions] = useState(false);

  useEffect(() => {
    fetchExamDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examId]);

  // Enter fullscreen mode when instructions page loads
  useEffect(() => {
    const enterFullscreen = async () => {
      try {
        const elem = document.documentElement;
        if (elem.requestFullscreen) {
          await elem.requestFullscreen();
        } else if (elem.webkitRequestFullscreen) {
          await elem.webkitRequestFullscreen();
        } else if (elem.msRequestFullscreen) {
          await elem.msRequestFullscreen();
        }
        showSuccess('Fullscreen mode activated');
      } catch (error) {
        console.error('Failed to enter fullscreen:', error);
        showWarning('Please allow fullscreen mode for the exam');
      }
    };

    enterFullscreen();

    // Do NOT exit fullscreen when leaving - keep it active for the exam page
  }, []);

  const fetchExamDetails = async () => {
    try {
      // Fetch exam details
      const examDoc = await getDoc(doc(db, 'exams', examId));
      if (!examDoc.exists()) {
        showError('Exam not found');
        navigate('/student-dashboard');
        return;
      }

      const examData = { id: examDoc.id, ...examDoc.data() };
      setExam(examData);

      // Check student's previous attempts (only count completed submissions)
      const studentId = localStorage.getItem('studentId');
      const attemptsQuery = query(
        collection(db, 'examSubmissions'),
        where('examId', '==', examId),
        where('studentId', '==', studentId),
        where('status', '==', 'completed')
      );
      const attemptsSnapshot = await getDocs(attemptsQuery);
      const attempts = attemptsSnapshot.size;
      setAttemptCount(attempts);

      // Check if student can take exam
      const maxAttempts = examData.attemptLimit === 'unlimited' 
        ? Infinity 
        : examData.attemptLimit === 'custom'
        ? parseInt(examData.customAttempts)
        : examData.attemptLimit === 'once'
        ? 1
        : parseInt(examData.attemptLimit);

      if (attempts >= maxAttempts && maxAttempts !== Infinity) {
        setCanTakeExam(false);
      }

    } catch (error) {
      console.error('Error fetching exam:', error);
      showError('Failed to load exam details');
    } finally {
      setLoading(false);
    }
  };

  const requestCameraPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      // Stop the stream immediately after getting permission
      stream.getTracks().forEach(track => track.stop());
      setCameraPermission('granted');
      return true;
    } catch (error) {
      console.error('Camera permission denied:', error);
      setCameraPermission('denied');
      showError('Camera permission is required to take the exam');
      return false;
    }
  };

  const requestLocationPermission = async () => {
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });
      setLocationPermission('granted');
      console.log('Location permission granted:', position.coords);
      return true;
    } catch (error) {
      console.error('Location permission denied:', error);
      setLocationPermission('denied');
      showError('Location permission is required to take the exam');
      return false;
    }
  };

  const handleStartExam = async () => {
    if (!agreed) {
      showError('Please accept the terms and conditions');
      return;
    }

    if (!canTakeExam) {
      showError('You have exceeded the maximum number of attempts');
      return;
    }

    setRequestingPermissions(true);

    // Request camera permission
    const cameraGranted = await requestCameraPermission();
    if (!cameraGranted) {
      setRequestingPermissions(false);
      return;
    }

    // Request location permission
    const locationGranted = await requestLocationPermission();
    if (!locationGranted) {
      setRequestingPermissions(false);
      return;
    }

    // Ensure we're still in fullscreen (backup check)
    if (!document.fullscreenElement) {
      try {
        const elem = document.documentElement;
        if (elem.requestFullscreen) {
          await elem.requestFullscreen();
        } else if (elem.webkitRequestFullscreen) {
          await elem.webkitRequestFullscreen();
        } else if (elem.msRequestFullscreen) {
          await elem.msRequestFullscreen();
        }
      } catch (error) {
        console.error('Fullscreen request failed:', error);
      }
    }

    setRequestingPermissions(false);

    // Navigate to exam page
    navigate(`/take-exam/${examId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto"></div>
          <p className="text-gray-600 mt-4 font-medium">Loading exam details...</p>
        </div>
      </div>
    );
  }

  if (!exam) return null;

  const getAttemptText = () => {
    if (exam.attemptLimit === 'unlimited') return 'Unlimited';
    if (exam.attemptLimit === 'custom') return exam.customAttempts;
    if (exam.attemptLimit === 'once') return '1';
    return exam.attemptLimit;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {exam.bannerImage && (
            <div className="w-full h-48 relative">
              <img 
                src={exam.bannerImage} 
                alt={exam.name} 
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
            </div>
          )}
          
          <div className="p-8">
            {/* Exam Title */}
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{exam.name}</h1>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm font-semibold">
                  {exam.totalQuestions} Questions
                </span>
                <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
                  {exam.totalMarks} Marks
                </span>
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                  {exam.duration} Minutes
                </span>
                {exam.hasNegativeMarking && (
                  <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-semibold">
                    ⚠ Negative Marking
                  </span>
                )}
                {exam.attemptLimit !== 'unlimited' && (
                  <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-semibold">
                    {attemptCount}/{getAttemptText()} Attempts Used
                  </span>
                )}
              </div>
            </div>

            {/* Fullscreen Mode Indicator */}
            <div className="mb-6 p-4 bg-indigo-50 border-l-4 border-indigo-500 rounded-lg">
              <div className="flex items-start gap-3">
                <svg className="h-6 w-6 text-indigo-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
                <div>
                  <h3 className="text-indigo-900 font-bold">🔒 Fullscreen Mode Active</h3>
                  <p className="text-indigo-700 text-sm mt-1">
                    This exam is in fullscreen mode for security. Please do not press ESC or attempt to exit fullscreen until you complete the exam.
                  </p>
                </div>
              </div>
            </div>

            {/* Warning if max attempts reached */}
            {!canTakeExam && (
              <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg className="h-6 w-6 text-red-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <h3 className="text-red-900 font-bold">Maximum Attempts Reached</h3>
                    <p className="text-red-700 text-sm mt-1">
                      You have used all {getAttemptText()} attempts for this exam. You cannot take this exam again.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Instructions */}
            <div className="space-y-6">
              {/* Exam Overview Box */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 p-6 rounded-xl">
                <h2 className="text-lg font-bold text-indigo-900 mb-4">📋 Exam Overview</h2>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div className="bg-white p-3 rounded-lg">
                    <span className="text-gray-600">Total Questions:</span>
                    <span className="font-bold text-gray-900 ml-2">{exam.totalQuestions}</span>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <span className="text-gray-600">Total Marks:</span>
                    <span className="font-bold text-gray-900 ml-2">{exam.totalMarks}</span>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <span className="text-gray-600">Duration:</span>
                    <span className="font-bold text-gray-900 ml-2">{exam.duration} minutes</span>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <span className="text-gray-600">Marks per Question:</span>
                    <span className="font-bold text-gray-900 ml-2">
                      {exam.marksPerQuestion ? `${exam.marksPerQuestion} marks` : 'Varies'}
                    </span>
                  </div>
                  {exam.hasNegativeMarking && (
                    <>
                      <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                        <span className="text-red-700">Negative Marking:</span>
                        <span className="font-bold text-red-900 ml-2">Yes (-{exam.negativeMarks} per wrong)</span>
                      </div>
                      <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                        <span className="text-yellow-700">Unanswered:</span>
                        <span className="font-bold text-yellow-900 ml-2">No penalty (0 marks)</span>
                      </div>
                    </>
                  )}
                  <div className="bg-white p-3 rounded-lg">
                    <span className="text-gray-600">Attempts Allowed:</span>
                    <span className="font-bold text-gray-900 ml-2">
                      {exam.attemptLimit === 'unlimited' ? 'Unlimited' : 
                       exam.attemptLimit === 'custom' ? exam.customAttempts :
                       exam.attemptLimit === 'once' ? '1 (Only Once)' : exam.attemptLimit}
                    </span>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <span className="text-gray-600">Results:</span>
                    <span className="font-bold text-gray-900 ml-2">
                      {exam.showResultImmediately ? 'Immediate' : 'After Publishing'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Negative Marking Details - Only show if enabled */}
              {exam.hasNegativeMarking && (
                <div className="bg-red-50 border-2 border-red-300 p-6 rounded-xl">
                  <h2 className="text-lg font-bold text-red-900 mb-3 flex items-center gap-2">
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    ⚠️ NEGATIVE MARKING ALERT
                  </h2>
                  <div className="space-y-2 text-sm">
                    <p className="text-red-800">
                      <strong>This exam has negative marking enabled.</strong> Please read carefully:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-red-700 ml-4">
                      <li><strong>Correct Answer:</strong> You will get full marks ({exam.marksPerQuestion || 'as specified'} marks)</li>
                      <li><strong>Wrong Answer:</strong> <span className="text-red-900 font-bold">-{exam.negativeMarks} marks</span> will be deducted</li>
                      <li><strong>Unanswered:</strong> No marks deducted (0 marks)</li>
                    </ul>
                    <p className="text-red-800 mt-3 font-semibold bg-red-100 p-2 rounded border border-red-300">
                      💡 Tip: Only attempt questions you are confident about. Leave uncertain questions unanswered to avoid negative marks!
                    </p>
                  </div>
                </div>
              )}

              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="h-6 w-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  General Instructions
                </h2>
                
                <div className="space-y-4 bg-gray-50 p-6 rounded-xl">
                  <div className="flex items-start gap-3">
                    <div className="bg-indigo-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Time Limit</h3>
                      <p className="text-gray-700 text-sm">You have <strong>{exam.duration} minutes</strong> to complete this exam. The exam will auto-submit when time runs out.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="bg-indigo-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Navigation</h3>
                      <p className="text-gray-700 text-sm">You can navigate between questions using the question palette. Unanswered questions will be highlighted.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="bg-indigo-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Auto-Save</h3>
                      <p className="text-gray-700 text-sm">Your answers are automatically saved as you type. Don't worry about losing progress.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="bg-red-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">4</div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Fullscreen Mode (Active)</h3>
                      <p className="text-gray-700 text-sm">This page is in <strong>fullscreen mode</strong> for your security. The exam will continue in fullscreen. Do not press ESC or exit fullscreen during the exam as it may be flagged as a security violation.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="bg-indigo-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">5</div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Mark for Review</h3>
                      <p className="text-gray-700 text-sm">You can bookmark questions you want to review later before final submission.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="bg-indigo-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">6</div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Submission</h3>
                      <p className="text-gray-700 text-sm">Review all your answers before final submission. Once submitted, you cannot change your answers.</p>
                    </div>
                  </div>

                  {exam.attemptLimit !== 'unlimited' && (
                    <div className="flex items-start gap-3">
                      <div className="bg-orange-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">7</div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Attempt Limit</h3>
                        <p className="text-gray-700 text-sm">
                          This exam allows <strong className="text-orange-600">{getAttemptText()} attempt(s)</strong> only. 
                          You have already used <strong className="text-orange-600">{attemptCount}</strong> attempt(s).
                          {(parseInt(getAttemptText()) - attemptCount) === 1 && ' This is your last attempt!'}
                          {attemptCount === 0 && ' Make sure you are fully prepared before starting.'}
                        </p>
                      </div>
                    </div>
                  )}

                  {exam.showResultImmediately && (
                    <div className="flex items-start gap-3">
                      <div className="bg-green-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">✓</div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Instant Results</h3>
                        <p className="text-gray-700 text-sm">
                          Your results will be displayed <strong className="text-green-600">immediately</strong> after you submit the exam.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Scoring System */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  Scoring System for This Exam
                </h2>
                <div className="bg-purple-50 border-2 border-purple-200 p-6 rounded-xl">
                  <div className="grid md:grid-cols-3 gap-4 text-center">
                    <div className="bg-white p-4 rounded-lg border-2 border-green-200">
                      <div className="text-3xl font-bold text-green-600 mb-2">
                        +{exam.marksPerQuestion || (exam.totalMarks / exam.totalQuestions).toFixed(2)}
                      </div>
                      <div className="text-sm font-semibold text-gray-900">Correct Answer</div>
                      <div className="text-xs text-gray-600 mt-1">Full marks awarded</div>
                    </div>
                    
                    {exam.hasNegativeMarking ? (
                      <>
                        <div className="bg-white p-4 rounded-lg border-2 border-red-200">
                          <div className="text-3xl font-bold text-red-600 mb-2">
                            -{exam.negativeMarks}
                          </div>
                          <div className="text-sm font-semibold text-gray-900">Wrong Answer</div>
                          <div className="text-xs text-gray-600 mt-1">Marks deducted</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border-2 border-yellow-200">
                          <div className="text-3xl font-bold text-yellow-600 mb-2">
                            0
                          </div>
                          <div className="text-sm font-semibold text-gray-900">Unanswered</div>
                          <div className="text-xs text-gray-600 mt-1">No penalty</div>
                        </div>
                      </>
                    ) : (
                      <div className="bg-white p-4 rounded-lg border-2 border-gray-200 md:col-span-2">
                        <div className="text-3xl font-bold text-gray-600 mb-2">
                          0
                        </div>
                        <div className="text-sm font-semibold text-gray-900">Wrong / Unanswered</div>
                        <div className="text-xs text-gray-600 mt-1">No negative marking in this exam</div>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-4 p-3 bg-white rounded-lg border border-purple-200">
                    <p className="text-sm text-purple-900 text-center">
                      <strong>Maximum Score:</strong> {exam.totalMarks} marks | 
                      <strong className="ml-2">Total Questions:</strong> {exam.totalQuestions} | 
                      {exam.hasNegativeMarking && (
                        <strong className="ml-2 text-red-600">Minimum Possible: {exam.totalMarks - (exam.totalQuestions * parseFloat(exam.negativeMarks))}</strong>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Important Rules */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Important Rules
                </h2>
                
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg space-y-3">
                  <p className="text-red-900 text-sm flex items-start gap-2">
                    <svg className="h-5 w-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span><strong>Do not refresh</strong> the page during the exam. This may cause loss of progress.</span>
                  </p>
                  <p className="text-red-900 text-sm flex items-start gap-2">
                    <svg className="h-5 w-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span><strong>Do not close</strong> the browser tab or window during the exam.</span>
                  </p>
                  <p className="text-red-900 text-sm flex items-start gap-2">
                    <svg className="h-5 w-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span><strong>Ensure stable internet</strong> connection throughout the exam duration.</span>
                  </p>
                  <p className="text-red-900 text-sm flex items-start gap-2">
                    <svg className="h-5 w-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span><strong>No external resources</strong> or help is allowed during the exam.</span>
                  </p>
                </div>
              </div>

              {/* Terms and Conditions */}
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <label className="flex items-start gap-4 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    disabled={!canTakeExam}
                    className="mt-1 w-6 h-6 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 disabled:opacity-50"
                  />
                  <div>
                    <span className="text-gray-900 font-semibold group-hover:text-indigo-600 transition">
                      I have read and understood all the instructions
                    </span>
                    <p className="text-sm text-gray-600 mt-1">
                      I agree to follow all the rules and regulations mentioned above. I understand that any violation may result in disqualification.
                    </p>
                  </div>
                </label>
              </div>

              {/* Permission Status */}
              {(cameraPermission || locationPermission) && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-2">Permission Status:</h4>
                  <div className="space-y-1 text-sm">
                    {cameraPermission && (
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${
                          cameraPermission === 'granted' ? 'bg-green-500' : 'bg-red-500'
                        }`}></span>
                        <span className="text-gray-700">
                          Camera: {cameraPermission === 'granted' ? 'Granted ✓' : 'Denied ✗'}
                        </span>
                      </div>
                    )}
                    {locationPermission && (
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${
                          locationPermission === 'granted' ? 'bg-green-500' : 'bg-red-500'
                        }`}></span>
                        <span className="text-gray-700">
                          Location: {locationPermission === 'granted' ? 'Granted ✓' : 'Denied ✗'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                <button
                  onClick={() => navigate('/student-dashboard')}
                  className="flex-1 px-8 py-4 bg-gray-200 text-gray-700 rounded-xl font-bold text-lg hover:bg-gray-300 transition"
                >
                  ← Back to Dashboard
                </button>
                <button
                  onClick={handleStartExam}
                  disabled={!agreed || !canTakeExam || requestingPermissions}
                  className={`flex-1 px-8 py-4 rounded-xl font-bold text-lg transition ${
                    agreed && canTakeExam && !requestingPermissions
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-2xl transform hover:scale-105'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {requestingPermissions ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Requesting Permissions...
                    </span>
                  ) : canTakeExam ? 'Start Exam →' : 'Cannot Start Exam'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamInstructions;
